import { useEffect, useRef, useState } from 'react';

/** How often new items may refresh the open table during a dig. */
export const LIVE_REFRESH_MS = 10_000;

/**
 * A slowed-down copy of a value that changes with every new item.
 *
 * DURING A DIG AN ITEM LANDS EVERY FEW SECONDS. Refetching the table, the
 * filter counts and the profile on each one queued request behind request on
 * the server, and each new one threw away the one still running, so a busy
 * room could sit on "Reading the board…" for two minutes, and a lens change
 * waited at the back of that queue.
 *
 * This hands the value on at most once per `ms`, and not while `busy` (a
 * refresh still running): the next one starts when that finishes. The first
 * value is handed on at once.
 */
export default function useLiveTick<T>(value: T, busy: boolean, ms: number = LIVE_REFRESH_MS): T {
  const [out, setOut] = useState(value);
  const last = useRef(0);
  useEffect(() => {
    if (Object.is(value, out) || busy) return;
    const wait = Math.max(0, last.current + ms - Date.now());
    const t = window.setTimeout(() => {
      last.current = Date.now();
      setOut(value);
    }, wait);
    return () => window.clearTimeout(t);
  }, [value, out, busy, ms]);
  return out;
}
